"use client";

import { useMemo, useState } from "react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import {
  Ban,
  CalendarClock,
  CalendarPlus,
  CheckCheck,
  Clock,
  MapPin,
  Pencil,
  type LucideIcon,
} from "lucide-react";
import {
  bySchedule,
  cn,
  formatTime,
  inScope,
  iso,
  ordinal,
  targetEmployees,
  teamIndex,
  withAlpha,
  ALL_SCOPE,
  STATUS_META,
  STATUS_ORDER,
  type Employee,
  type Itinerary,
  type Scope,
  type Status,
} from "@routecore/core";
import { useStudio } from "@routecore/app-shell";
import { EmployeeDot, ScopeFilter, PrimaryButton, ConfirmDelete } from "@routecore/controls";
import { RollingNumber, Tilt3D } from "@routecore/motion-fx";

const BOARD_ICONS: Record<Status, LucideIcon> = {
  scheduled: CalendarClock,
  completed: CheckCheck,
  cancelled: Ban,
};

export default function TodayView() {
  const {
    employees,
    teams,
    itineraries,
    openItineraryModal,
    openEmployeeModal,
    openTeamModal,
    updateItinerary,
    removeItinerary,
    currentUser,
    isPrivileged,
    canEditEmployee,
    canEditItinerary,
  } = useStudio();
  const [scope, setScope] = useState<Scope>(ALL_SCOPE);
  const teamOf = useMemo(() => teamIndex(employees), [employees]);

  const anyWritable = useMemo(
    () => isPrivileged || employees.some((e) => canEditEmployee(e)),
    [employees, isPrivileged, canEditEmployee],
  );
  const isSuperAdmin = useMemo(
    () => !!currentUser?.is_super_admin,
    [currentUser],
  );

  const today = new Date();
  const todayStr = iso(today);

  const visibleEmployees = useMemo(
    () => targetEmployees(employees, scope),
    [employees, scope],
  );

  const todays = useMemo(
    () => itineraries.filter((i) => i.date === todayStr && inScope(i, scope, teamOf)),
    [itineraries, todayStr, scope, teamOf],
  );

  const counts = useMemo(() => {
    const c: Record<Status, number> = { scheduled: 0, completed: 0, cancelled: 0 };
    for (const it of todays) c[it.status] += 1;
    return c;
  }, [todays]);

  const byEmployee = useMemo(() => {
    const map = new Map<number, Itinerary[]>();
    for (const it of todays) {
      const list = map.get(it.employeeId) ?? [];
      list.push(it);
      map.set(it.employeeId, list);
    }
    for (const list of map.values()) list.sort(bySchedule);
    return map;
  }, [todays]);

  return (
    <div className="mx-auto w-full max-w-[1600px] 2xl:max-w-none">
      {/* header */}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="relative flex size-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-60" />
              <span className="relative inline-flex size-2.5 rounded-full bg-accent" />
            </span>
            <span className="text-[10px] font-bold tracking-[0.24em] text-ink-faint uppercase">
              Live · Status board
            </span>
          </div>
          <h1 className="mt-2 font-display text-4xl font-semibold tracking-tight sm:text-5xl">
            {format(today, "EEEE")}
            <span className="text-ink-faint">, {format(today, "MMMM d")}</span>
          </h1>
        </div>
        <PrimaryButton
          icon={CalendarPlus}
          disabled={!anyWritable}
          onClick={() =>
            openItineraryModal({
              presetDate: todayStr,
              presetEmployeeId:
                scope.employee !== "all"
                  ? scope.employee
                  : visibleEmployees.length === 1
                    ? visibleEmployees[0].id
                    : undefined,
            })
          }
        >
          {anyWritable ? "New itinerary" : "🔒 No bound profile"}
        </PrimaryButton>
      </header>

      {/* team + employee filter */}
      <div className="mt-6">
        <ScopeFilter
          employees={employees}
          teams={teams}
          scope={scope}
          onChange={setScope}
          onAdd={isPrivileged ? () => openEmployeeModal() : undefined}
          onManageTeams={isPrivileged ? openTeamModal : undefined}
        />
      </div>

      {/* status summary strip */}
      <div className="mt-4 overflow-hidden rounded-2xl border border-line bg-porcelain shadow-card">
        <div className="grid grid-cols-3">
          {STATUS_ORDER.map((s, i) => {
            const meta = STATUS_META[s];
            const Icon = BOARD_ICONS[s];
            return (
              <div key={s} className="flex items-center gap-3 border-line px-4 py-4 not-last:border-r sm:px-5">
                <span
                  className="grid size-9 shrink-0 place-items-center rounded-xl"
                  style={{ backgroundColor: withAlpha(meta.color, 0.12), color: meta.color }}
                >
                  <Icon className="size-4" />
                </span>
                <div className="min-w-0">
                  <div className="font-display text-2xl leading-none font-semibold">
                    <RollingNumber value={counts[s]} delay={0.15 + i * 0.1} />
                  </div>
                  <div className="mt-1 truncate text-[10px] font-bold tracking-[0.14em] text-ink-soft uppercase">
                    {meta.label}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {/* segmented progress */}
        <div className="flex h-1.5 w-full bg-cream">
          {STATUS_ORDER.map((s) => (
            <motion.span
              key={s}
              initial={{ width: 0 }}
              animate={{ width: `${(counts[s] / (todays.length || 1)) * 100}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              style={{ backgroundColor: STATUS_META[s].color, display: "block" }}
            />
          ))}
        </div>
      </div>

      {/* employee cards */}
      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {visibleEmployees.map((emp, idx) => {
          const empWritable = canEditEmployee(emp);
          return (
            <EmployeeCard
              key={emp.id}
              emp={emp}
              items={byEmployee.get(emp.id) ?? []}
              index={idx}
              empWritable={empWritable}
              canEditItinerary={canEditItinerary}
              statusEditable={isSuperAdmin}
              onQuickAdd={() =>
                openItineraryModal({ presetDate: todayStr, presetEmployeeId: emp.id })
              }
              onEdit={(it) => openItineraryModal({ itinerary: it })}
              onDelete={(it) => removeItinerary(it.id)}
              onSetStatus={(it, s) => updateItinerary(it.id, { status: s }, { silent: true })}
            />
          );
        })}
      </div>

      {visibleEmployees.length === 0 && (
        <div className="mt-10 grid place-items-center rounded-3xl border border-dashed border-line py-16 text-center">
          <p className="font-display text-xl text-ink-soft italic">No employees match this filter.</p>
        </div>
      )}
    </div>
  );
}

function EmployeeCard({
  emp,
  items,
  index,
  empWritable,
  canEditItinerary,
  statusEditable,
  onQuickAdd,
  onEdit,
  onDelete,
  onSetStatus,
}: {
  emp: Employee;
  items: Itinerary[];
  index: number;
  empWritable: boolean;
  canEditItinerary: (it: Pick<Itinerary, "employeeId">) => boolean;
  statusEditable: boolean;
  onQuickAdd: () => void;
  onEdit: (it: Itinerary) => void;
  onDelete: (it: Itinerary) => void;
  onSetStatus: (it: Itinerary, s: Status) => void;
}) {
  const boardState = useMemo(() => {
    if (items.length === 0) return { label: "Off duty", color: "#A89C85" };
    const pending = items.some((i) => i.status === "scheduled");
    if (pending) return { label: "Scheduled", color: "#5B6B8C" };
    if (items.every((i) => i.status === "completed")) return { label: "All done", color: "#1F8A70" };
    if (items.every((i) => i.status === "cancelled")) return { label: "Stand down", color: "#B4443C" };
    return { label: "Wrapped up", color: "#756A56" };
  }, [items]);

  return (
    <motion.section
      initial={{ opacity: 0, y: 22, rotateX: -8 }}
      animate={{ opacity: 1, y: 0, rotateX: 0 }}
      transition={{ delay: 0.1 + index * 0.07, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      style={{ transformPerspective: 1100 }}
    >
      <Tilt3D radius="1rem" intensity={7} lift={-6} scale={1.015} className="h-full">
        <div className="flex h-full flex-col rounded-2xl border border-line bg-porcelain p-4 shadow-card">
      <header className="relative flex items-center gap-3" style={{ transform: "translateZ(28px)" }}>
        <EmployeeDot name={emp.name} color={emp.color} size={42} />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[15px] font-bold">{emp.name}</span>
            {!empWritable && (
              <span
                title={`Read-only — bound to ${emp.boundAccountName ?? emp.boundAccountEmail ?? "another account"}.`}
                className="shrink-0 text-[10px] text-ink-faint"
              >
                🔒
              </span>
            )}
          </div>
          <div className="truncate text-xs text-ink-faint">{emp.role || "Team member"}</div>
        </div>
        <span
          className="ml-auto inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold tracking-wide uppercase"
          style={{
            color: boardState.color,
            backgroundColor: withAlpha(boardState.color, 0.12),
            border: `1px solid ${withAlpha(boardState.color, 0.3)}`,
          }}
        >
          <span className="size-1.5 rounded-full" style={{ backgroundColor: boardState.color }} />
          {boardState.label}
        </span>
      </header>

      <div className="mt-3.5 flex-1 space-y-2.5">
        {items.map((it) => {
          const itWritable = canEditItinerary(it);
          return (
            <div
              key={it.id}
              className={cn(
                "rounded-xl border p-3 transition-colors",
                itWritable
                  ? "border-line bg-paper/60 hover:border-ink/25"
                  : "border-line/70 bg-paper/40 opacity-95",
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-start gap-2">
                  <span className="mt-0.5 shrink-0 rounded-md bg-ink/[0.06] px-1.5 py-0.5 text-[9px] font-black tracking-wider text-ink-soft uppercase">
                    {ordinal(it.seq)}
                  </span>
                  {it.appointmentTime && (
                    <span
                      className="mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-black tabular-nums"
                      style={{ backgroundColor: withAlpha(emp.color, 0.14), color: emp.color }}
                    >
                      <Clock className="size-2.5" />
                      {formatTime(it.appointmentTime)}
                    </span>
                  )}
                  <p
                    className={cn(
                      "min-w-0 text-sm leading-snug font-semibold",
                      it.status === "cancelled" && "text-ink-faint line-through",
                    )}
                  >
                    {it.title}
                  </p>
                </div>
                {itWritable && (
                  <div className="flex items-center gap-0.5">
                    <button
                      type="button"
                      title="Edit itinerary"
                      onClick={() => onEdit(it)}
                      className="grid size-6 shrink-0 place-items-center rounded-md text-ink-faint transition-colors hover:bg-ink/10 hover:text-ink"
                    >
                      <Pencil className="size-3" />
                    </button>
                    <ConfirmDelete onConfirm={() => onDelete(it)} />
                  </div>
                )}
                {!itWritable && (
                  <span
                    title="Read-only — bound to another account."
                    className="mt-1 shrink-0 text-[9px] text-ink-faint"
                  >
                    🔒
                  </span>
                )}
              </div>
              {it.locationName && (
                <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-ink-soft">
                  <MapPin className="size-3 shrink-0 text-accent" />
                  <span className="truncate">{it.locationName}</span>
                </div>
              )}
              {it.notes && <p className="mt-1 text-xs leading-relaxed text-ink-soft">{it.notes}</p>}
              {itWritable && (
                <div className="mt-2.5 flex flex-wrap items-center gap-1">
                  {!statusEditable && (
                    <span
                      title="Only super admins can change itinerary status."
                      className="shrink-0 text-[9px] text-ink-faint"
                    >
                      🔒
                    </span>
                  )}
                  {STATUS_ORDER.map((s) => {
                    const meta = STATUS_META[s];
                    const active = it.status === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        disabled={!statusEditable}
                        title={
                          statusEditable
                            ? meta.description
                            : "Only super admins can change itinerary status."
                        }
                        onClick={() => onSetStatus(it, s)}
                        className={cn(
                          "rounded-full border px-2 py-1 text-[9px] font-bold tracking-wider uppercase transition-all active:scale-95",
                          statusEditable
                            ? active
                              ? "shadow-sm"
                              : "border-line text-ink-faint hover:border-ink/30 hover:text-ink"
                            : "border-line text-ink-faint cursor-not-allowed opacity-70",
                        )}
                        style={
                          active
                            ? {
                                color: meta.color,
                                borderColor: withAlpha(meta.color, 0.5),
                                backgroundColor: withAlpha(meta.color, 0.12),
                              }
                            : undefined
                        }
                      >
                        {meta.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        <button
          type="button"
          onClick={onQuickAdd}
          disabled={!empWritable}
          title={empWritable ? undefined : "Ask an admin to bind this profile to your account."}
          className={cn(
            "grid w-full place-items-center gap-1 rounded-xl border border-dashed py-5 transition-colors",
            empWritable
              ? "text-ink-faint hover:border-accent hover:text-accent"
              : "cursor-not-allowed border-line/60 text-ink-faint/60",
            items.length === 0 ? "border-line" : "border-line/70 py-3",
          )}
        >
          <CalendarPlus className="size-4" />
          <span className="text-[11px] font-bold">
            {empWritable
              ? items.length === 0
                ? "Assign today's itinerary"
                : "Add another stop"
              : "🔒 Read-only"}
          </span>
        </button>
          </div>
        </div>
      </Tilt3D>
    </motion.section>
  );
}
